import type { TFunction } from "i18next";

/**
 * The cadence sentence, in one place.
 *
 * Three shapes come out of the same billing period, and each of them is a different sentence in
 * French, so none of them is built by gluing a number to a noun at a call site:
 *
 * - `cadence` sits after an amount: "79 EUR a month", "79 EUR par mois", "every 3 months".
 * - `everyPhrase` sits after a verb: "Billed every month", "Facturé tous les mois".
 * - `duration` says how long a commitment lasts: "one month", "3 months".
 *
 * The plural comes from i18next's `count`, which resolves through Intl.PluralRules, so a
 * language with a different plural table needs no code here.
 */

export interface Cadence {
  interval: string;
  intervalCount: number;
}

const KNOWN = new Set(["day", "week", "month", "year"]);

/** "a month" / "par mois" / "every 3 months". Falls back to the one-off sentence. */
export function cadenceLine(
  t: TFunction,
  cadence: Cadence | null | undefined,
): string {
  if (!cadence || !KNOWN.has(cadence.interval)) {
    return t("gymkeePay.common.cadence.oneTime");
  }
  return t(`gymkeePay.common.cadence.${cadence.interval}`, {
    count: Math.max(1, Math.round(cadence.intervalCount)),
  });
}

/** "every month" / "tous les mois", for a sentence that already carries its verb. */
export function everyPhrase(t: TFunction, cadence: Cadence): string | null {
  if (!KNOWN.has(cadence.interval)) return null;
  return t(`gymkeePay.common.every.${cadence.interval}`, {
    count: Math.max(1, Math.round(cadence.intervalCount)),
  });
}

/** "one month" / "3 months", for the minimum commitment caption. */
export function durationPhrase(t: TFunction, cadence: Cadence): string | null {
  if (!KNOWN.has(cadence.interval)) return null;
  return t(`gymkeePay.common.duration.${cadence.interval}`, {
    count: Math.max(1, Math.round(cadence.intervalCount)),
  });
}

/** "{{product}}, first month". One key per interval, because French genders the ordinal. */
export function firstPeriodLine(
  t: TFunction,
  product: string,
  cadence: Cadence | null | undefined,
): string {
  if (!cadence || !KNOWN.has(cadence.interval)) return product;
  return t(`gymkeePay.checkout.firstPeriod.${cadence.interval}`, { product });
}
